import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import {
  systemChangeBundleListApiCall,
  systemChangeBundleDataApiCall,
  systemChangeBundleCreateApiCall,
  systemChangeBundleUpdateApiCall,
  systemChangeBundleDeleteApiCall,
  systemChangeBundleReleaseApiCall,
  systemChangeAssignBundleApiCall,
} from "../../../../helpers/fakebackend_helper";
import { ColourChip, bundleLabel, formatDate, isReleased } from "./shared";

const EMPTY_FORM = {
  Name: "",
  VersionLabel: "",
  PlannedReleaseDate: "",
  Description: "",
  GitHubCommitHash: "",
  ReleaseNotes: "",
};

/**
 * System Changes (CRMit Task 24912) - bundles. A bundle is a release: done
 * changes are put into one (from the Ready to bundle tab, or a change's own
 * Bundle field), then the bundle is released as one. Released bundles are
 * closed until re-opened.
 */
const BundlesTab = ({ onChanged, onOpenChange, refreshKey, openCreate, onCreateOpened }) => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);

  const [viewBundle, setViewBundle] = useState(null);
  const [viewChanges, setViewChanges] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  const [confirm, setConfirm] = useState(null); // { kind: "delete" | "release" | "reopen", bundle }

  const load = async () => {
    setLoading(true);
    try {
      const response = await systemChangeBundleListApiCall();
      setBundles(response?.systemchangebundle_list || []);
    } catch (e) {
      toast.error("Could not load bundles");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const openView = async (bundle) => {
    setViewBundle(bundle);
    setViewLoading(true);
    try {
      const response = await systemChangeBundleDataApiCall(bundle.Id);
      setViewBundle(response?.systemchangebundle_data || bundle);
      setViewChanges(response?.systemchange_list || []);
    } catch (e) {
      toast.error("Could not load the bundle");
    }
    setViewLoading(false);
  };

  const refreshAll = () => {
    load();
    onChanged?.();
    if (viewBundle) openView(viewBundle);
  };

  const openForm = (bundle) => {
    setEditId(bundle?.Id || null);
    setForm(
      bundle
        ? Object.fromEntries(
            Object.keys(EMPTY_FORM).map((key) => [key, bundle[key] || ""])
          )
        : EMPTY_FORM
    );
    setFormOpen(true);
  };

  // Change #100004 - the screen's own Create Bundle button lands here with
  // openCreate set; the parent clears it so a later remount does not reopen it.
  useEffect(() => {
    if (!openCreate) return;
    openForm(null);
    onCreateOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCreate]);

  const saveForm = async () => {
    const call = editId ? systemChangeBundleUpdateApiCall : systemChangeBundleCreateApiCall;
    try {
      const response = await call([{ ...(editId && { Id: editId }), ...form }]);
      if (response?.status === 200) {
        toast.success(editId ? "Bundle saved" : "Bundle created");
        setFormOpen(false);
        refreshAll();
      } else {
        toast.error(response?.message || "Could not save the bundle");
      }
    } catch (e) {
      toast.error("Could not save the bundle");
    }
  };

  const runConfirm = async () => {
    const { kind, bundle } = confirm;
    try {
      const response =
        kind === "delete"
          ? await systemChangeBundleDeleteApiCall({ Id: bundle.Id })
          : await systemChangeBundleReleaseApiCall([
              { Id: bundle.Id, FlagReleased: kind === "release" ? 1 : 0 },
            ]);
      if (response?.status === 200) {
        toast.success(response.message);
        if (kind === "delete" && viewBundle?.Id === bundle.Id) setViewBundle(null);
        setConfirm(null);
        refreshAll();
      } else {
        toast.error(response?.message || "Could not update the bundle");
      }
    } catch (e) {
      toast.error("Could not update the bundle");
    }
  };

  const removeFromBundle = async (change) => {
    try {
      const response = await systemChangeAssignBundleApiCall([
        { SystemChangeBundle_Id: null, SystemChange_Ids: [change.Id] },
      ]);
      if (response?.status === 200) refreshAll();
      else toast.error(response?.message || "Could not remove the change");
    } catch (e) {
      toast.error("Could not remove the change");
    }
  };

  // Release notes: one line per change, grouped by type, ready to paste.
  const copyReleaseNotes = () => {
    const groups = {};
    viewChanges.forEach((change) => {
      const type = change.SystemChangeType_Name || "Other";
      (groups[type] = groups[type] || []).push(`- #${change.Id} ${change.Title}`);
    });
    const text = [
      bundleLabel(viewBundle),
      "",
      ...Object.entries(groups).flatMap(([type, lines]) => [type, ...lines, ""]),
    ].join("\n");
    navigator.clipboard?.writeText(text);
    toast.success("Release notes copied");
  };

  // Change #100011: the bundle's changes as "<Title> Change #<Id>", one per line -
  // the shape the commit message for a release is written in. Uses the drawer's
  // list when it is showing this bundle, otherwise fetches it.
  const copyChangeList = async (bundle) => {
    try {
      let changes = viewChanges;
      if (!viewBundle || String(viewBundle.Id) !== String(bundle.Id)) {
        const response = await systemChangeBundleDataApiCall(bundle.Id);
        changes = response?.systemchange_list || [];
      }
      if (!changes.length) {
        toast.info("This bundle has no changes to copy");
        return;
      }
      const text = changes
        .map((change) => `${change.Title} Change #${change.Id}`)
        .join("\n");
      await navigator.clipboard.writeText(text);
      toast.success(
        `Copied ${changes.length} change${changes.length === 1 ? "" : "s"}`
      );
    } catch (e) {
      toast.error("Could not copy the change list");
    }
  };

  const term = keyword.trim().toLowerCase();
  const rows = bundles
    .filter(
      (bundle) =>
        !term ||
        [bundle.Name, bundle.VersionLabel, bundle.Description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
    )
    .map((bundle) => ({ ...bundle, id: bundle.Id }));

  const columns = [
    {
      field: "Name",
      headerName: "Bundle",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {isReleased(params.row) && <LockIcon fontSize="small" color="success" />}
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { field: "VersionLabel", headerName: "Version", width: 110 },
    {
      field: "CountChange",
      headerName: "Changes",
      width: 95,
      type: "number",
    },
    {
      field: "PlannedReleaseDate",
      headerName: "Planned",
      width: 110,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "ReleasedDateTime",
      headerName: "Released",
      width: 170,
      renderCell: (params) =>
        isReleased(params.row)
          ? `${formatDate(params.value, "DD/MM/YYYY HH:mm")}${
              params.row.ReleasedUser_FullName ? ` ${params.row.ReleasedUser_FullName}` : ""
            }`
          : "Open",
    },
    {
      field: "actions",
      headerName: "",
      width: 185,
      sortable: false,
      renderCell: (params) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Copy change IDs and titles">
            <span>
              <IconButton
                size="small"
                disabled={!Number(params.row.CountChange)}
                onClick={() => copyChangeList(params.row)}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openForm(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isReleased(params.row) ? "Re-open" : "Release"}>
            <IconButton
              size="small"
              color={isReleased(params.row) ? "default" : "success"}
              onClick={() =>
                setConfirm({
                  kind: isReleased(params.row) ? "reopen" : "release",
                  bundle: params.row,
                })
              }
            >
              {isReleased(params.row) ? (
                <LockOpenIcon fontSize="small" />
              ) : (
                <RocketLaunchIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={isReleased(params.row) ? "Re-open it to delete" : "Delete"}>
            <span>
              <IconButton
                size="small"
                disabled={isReleased(params.row)}
                onClick={() => setConfirm({ kind: "delete", bundle: params.row })}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const confirmText = {
    release: "Release this bundle? It is closed afterwards: nothing can be added or removed until it is re-opened.",
    reopen: "Re-open this bundle so changes can be added or removed again?",
    delete: "Delete this bundle? Its changes go back to unbundled, nothing is lost.",
  };

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between", mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search bundles..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForm(null)}>
          Create Bundle
        </Button>
      </Box>

      <Paper elevation={3} sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          onRowClick={(params) => openView(params.row)}
          sx={{ border: 0, "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Paper>

      <AppModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? "Edit bundle" : "Create bundle"}
        onSubmit={saveForm}
        submitDisabled={!form.Name.trim()}
        size="md"
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" }, gap: 2 }}>
          <TextField
            label="Name"
            value={form.Name}
            onChange={(e) => setForm({ ...form, Name: e.target.value })}
            autoFocus
            required
          />
          <TextField
            label="Version"
            value={form.VersionLabel}
            onChange={(e) => setForm({ ...form, VersionLabel: e.target.value })}
          />
          <TextField
            type="date"
            label="Planned release"
            value={form.PlannedReleaseDate ? form.PlannedReleaseDate.slice(0, 10) : ""}
            onChange={(e) => setForm({ ...form, PlannedReleaseDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <TextField
          label="Description"
          value={form.Description}
          onChange={(e) => setForm({ ...form, Description: e.target.value })}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <TextField
          label="Release notes"
          value={form.ReleaseNotes}
          onChange={(e) => setForm({ ...form, ReleaseNotes: e.target.value })}
          fullWidth
          multiline
          minRows={3}
          sx={{ mt: 2 }}
        />
        <TextField
          label="Git commit hash"
          value={form.GitHubCommitHash}
          onChange={(e) => setForm({ ...form, GitHubCommitHash: e.target.value })}
          fullWidth
          sx={{ mt: 2 }}
        />
      </AppModal>

      <AppModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm ? bundleLabel(confirm.bundle) : ""}
        onSubmit={runConfirm}
        submitLabel={
          confirm?.kind === "delete" ? "Delete" : confirm?.kind === "release" ? "Release" : "Re-open"
        }
        submitColor={confirm?.kind === "delete" ? "error" : "primary"}
      >
        <Typography>{confirm ? confirmText[confirm.kind] : ""}</Typography>
        {confirm?.kind === "release" && (
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            disabled={!Number(confirm.bundle?.CountChange)}
            onClick={() => copyChangeList(confirm.bundle)}
            sx={{ mt: 2 }}
          >
            Copy change list
          </Button>
        )}
      </AppModal>

      <Drawer
        anchor="right"
        open={!!viewBundle}
        onClose={() => setViewBundle(null)}
        PaperProps={{ sx: { width: { xs: "100%", md: 760 } } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1 }}>
            <Box>
              <Typography variant="h6">{bundleLabel(viewBundle)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {isReleased(viewBundle)
                  ? `Released ${formatDate(viewBundle?.ReleasedDateTime, "DD/MM/YYYY HH:mm")}`
                  : `Open${viewBundle?.PlannedReleaseDate ? ` - planned ${formatDate(viewBundle.PlannedReleaseDate)}` : ""}`}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                disabled={!viewChanges.length}
                onClick={copyReleaseNotes}
              >
                Copy release notes
              </Button>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                disabled={!viewChanges.length}
                onClick={() => copyChangeList(viewBundle)}
              >
                Copy change list
              </Button>
              <IconButton size="small" onClick={() => setViewBundle(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {viewBundle?.Description && (
            <Typography sx={{ mb: 2, whiteSpace: "pre-wrap" }}>{viewBundle.Description}</Typography>
          )}
          {viewBundle?.ReleaseNotes && (
            <Typography sx={{ mb: 2, whiteSpace: "pre-wrap" }} color="text.secondary">
              {viewBundle.ReleaseNotes}
            </Typography>
          )}

          {viewLoading && <CircularProgress size={22} />}
          {!viewLoading && viewChanges.length === 0 && (
            <Typography color="text.secondary">
              No changes yet. Add done changes from the Ready to bundle tab.
            </Typography>
          )}
          {!viewLoading &&
            viewChanges.map((change) => (
              <Box
                key={change.Id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.75,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ cursor: "pointer", flex: 1, minWidth: 0 }}
                  noWrap
                  onClick={() => onOpenChange?.(change.Id)}
                >
                  <strong>#{change.Id}</strong> {change.Title}
                </Typography>
                <ColourChip label={change.SystemChangeType_Name} />
                <ColourChip
                  label={change.SystemChangeStatus_Name}
                  background={change.SystemChangeStatus_CssColourBackground}
                />
                {!isReleased(viewBundle) && (
                  <Tooltip title="Take out of this bundle">
                    <IconButton size="small" onClick={() => removeFromBundle(change)}>
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
        </Box>
      </Drawer>
    </>
  );
};

export default BundlesTab;
